import { Box, Button, Grid, Stack, Typography, alpha } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { observer } from 'mobx-react-lite';
import { companyStore } from 'stores/companyStore';
import { personalDataStore } from 'stores/personalDataStore';
import type { VacancyLevel } from 'stores/vacancyStore';
import { useNavigate } from 'react-router-dom';
import { PATHS } from 'routes/paths';
import { interviewStore } from 'stores/interviewStore';

const LEVEL_LABELS: Record<VacancyLevel, string> = {
  JUNIOR: 'Начинающий',
  MIDDLE: 'С опытом',
  SENIOR: 'Эксперт'
};

const Overline = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="caption"
    sx={{
      display: 'block',
      fontWeight: 700,
      fontSize: 12,
      letterSpacing: '1px',
      textTransform: 'uppercase',
      color: 'text.disabled',
      mb: 1
    }}
  >
    {children}
  </Typography>
);

const VacancyInfoPage = observer(() => {
  const { id } = useParams<{ id: string }>();

  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      personalDataStore.getRequestedInterviews();
    }
  }, [id]);

  const vancy = useMemo(() => {
    return personalDataStore.requestedInterviews.find((interview) => interview.id === id);
  }, [id]);

  useEffect(() => {
    if (vancy) {
      companyStore.loadCompany(vancy.vacancy.companyId);
    }
  }, [vancy]);

  if (!vancy) {
    return (
      <Stack>
        <Typography color="text.secondary">Приглашение не найдено.</Typography>
      </Stack>
    );
  }

  const vacancy = vancy.vacancy;
  const company = companyStore.currentCompany;
  const level = vacancy.specialityLevel as VacancyLevel;
  const infoItems = [
    {
      label: 'Название компании',
      value: company?.name ?? '—',
      icon: 'mdi:office-building-outline',
      iconColor: 'primary.main'
    },
    {
      label: 'Email компании',
      value: company?.email ?? '—',
      icon: 'mdi:email-outline',
      iconColor: 'primary.main'
    },
    {
      label: 'Сфера деятельности',
      value: vacancy.speciality,
      icon: 'mdi:briefcase-outline',
      iconColor: 'primary.main'
    },
    {
      label: 'Уровень',
      value: LEVEL_LABELS[level] ?? vacancy.specialityLevel,
      icon: 'mdi:chart-timeline',
      iconColor: 'info.main'
    }
  ] as const;

  return (
    <Stack sx={{ height: '100%', overflow: 'auto', pb: 0 }} spacing={3}>
      <Box
        sx={{
          mt: 2,
          p: { xs: 3, sm: 4 },
          borderRadius: 3,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'grey.300'
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'grey.900', mb: 1 }}>
          Вас пригласили пройти видеоинтервью с помощью ИИ
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Вам будут заданы вопросы о ваших навыках и опыте работы. В конце вы получите детальную
          оценку и рекомендации по развитию, а также обратную связь от HR компании.
        </Typography>
      </Box>

      <Box
        sx={{
          p: 3,
          borderRadius: 3,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'grey.300', 
          mb: '16px !important'
        }}
      >
        <Overline>Вакансия</Overline>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
          {vacancy.position}
        </Typography>
        <Grid container spacing={2}>
          {infoItems.map(({ label, value, icon, iconColor }) => (
            <Grid item xs={12} sm={6} key={label}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'grey.100',
                  border: '1px solid',
                  borderColor: 'grey.300'
                }}
              >
                <Box
                  sx={(theme) => ({
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: iconColor,
                    bgcolor: alpha(
                      (() => {
                        const parts = iconColor.split('.');
                        const root = theme.palette[parts[0] as keyof typeof theme.palette] as any;
                        return (root?.[parts[1]] as string) || theme.palette.primary.main;
                      })(),
                      0.12
                    )
                  })}
                >
                  <Icon icon={icon} width={20} height={20} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      fontWeight: 700,
                      fontSize: 11,
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      color: 'text.disabled',
                      mb: 0.25
                    }}
                  >
                    {label}
                  </Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ color: 'text.primary' }}>
                    {value}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Button
        variant="contained"
        size="large"
        fullWidth
        onClick={() => {
          navigate(PATHS.requestedInterviews.checkDevices.replace(':id', id || ''));
        }}
        aria-label="Начать видеоинтервью"
        sx={{
          alignSelf: 'flex-end',
          fontWeight: 700,
          textTransform: 'none',
          borderRadius: 1,
          mt: 'auto !important'
        }}
      >
        Начать интервью
      </Button>
    </Stack>
  );
});

export default VacancyInfoPage;
